import { useCallback, useEffect, useRef } from 'react';
import axios from '../../utils/axios';

/**
 * Debounced background autosave for a wizard step.
 *
 * Returns `{ flush }` — and DELIBERATELY exposes no busy flag. A background
 * save must stay invisible to the UI, so there is nothing here to bind to a
 * `disabled` prop or to a footer button. Two bugs came out of exposing one:
 * browsers blur a focused element the instant it becomes disabled, so a field
 * disabled by a background save yanks the cursor out mid-typing (that was
 * PP-627 on Step 6's legal-name / legal-address fields), and a
 * `disabled={saving}` Next button silently swallows the click that follows a
 * type-then-pause. If a step needs a busy state, it owns a foreground flag set
 * by its own click handlers — disabling on click-initiated work is fine, the
 * host is already waiting on it. Don't reintroduce a flag here.
 *
 * Saves are SERIALIZED, not merely deduped: every save links onto `chainRef`, so
 * at most one `PUT /listings/:id` from this hook is ever in flight. That matters
 * because the fields stay live during a save — two overlapping PUTs would let a
 * slow older payload land last and overwrite what the host typed after it. Each
 * queued save reads `valueRef.current` when it actually runs, so a save that
 * waited its turn sends the newest value, and `lastSavedSignatureRef` always
 * reflects the last thing written.
 */
export default function useStepAutosave({
    listingId,
    hasAddress,
    value,
    buildPayload,
    debounceMs = 800,
}) {
    const timerRef = useRef(null);
    const lastSavedSignatureRef = useRef(null);
    const valueRef = useRef(value);
    const buildPayloadRef = useRef(buildPayload);
    // Tail of the serialized save chain. Never rejects (see enqueueSave).
    const chainRef = useRef(Promise.resolve());

    useEffect(() => { valueRef.current = value; }, [value]);
    useEffect(() => { buildPayloadRef.current = buildPayload; }, [buildPayload]);

    // Clear the dedup signature while autosave is gated off (no listingId
    // yet, or address not confirmed) so the first save after re-enablement
    // is guaranteed to fire. Do NOT pre-seed it with the mount-time payload
    // — that would silently mark form defaults as "already saved" and
    // short-circuit the first flush/debounce.
    useEffect(() => {
        if (!listingId || !hasAddress) {
            lastSavedSignatureRef.current = null;
        }
    }, [listingId, hasAddress]);

    // Both the chain and the dedup signature describe writes to ONE listing, so
    // they have to be dropped when the target changes — otherwise the new
    // listing's first save queues behind the old listing's in-flight PUT and is
    // deduped against the old listing's payload. Keyed on a real change rather
    // than folded into the effect above: replacing the chain tail on a
    // `hasAddress` flip could abandon a live save and let the next one overlap
    // it, which is exactly what the chain exists to prevent.
    const prevListingRef = useRef(listingId);
    useEffect(() => {
        if (prevListingRef.current === listingId) return;
        prevListingRef.current = listingId;
        lastSavedSignatureRef.current = null;
        chainRef.current = Promise.resolve();
    }, [listingId]);

    // Links one save onto the chain and returns a promise for THAT save, so a
    // caller can await its own write without inheriting an earlier failure.
    const enqueueSave = useCallback((valueOverride) => {
        if (!listingId || !hasAddress) return Promise.resolve();

        const result = chainRef.current.then(async () => {
            // Built here, not at enqueue time: a save that waited its turn must
            // send the newest value, not the one current when it queued.
            const currentValue = valueOverride !== undefined ? valueOverride : valueRef.current;
            const payload = buildPayloadRef.current(currentValue);
            const signature = JSON.stringify(payload);
            if (signature === lastSavedSignatureRef.current) return;

            await axios.put(`/listings/${listingId}`, payload);
            lastSavedSignatureRef.current = signature;
        });

        // The tail must be the rejection-swallowed version — a chain that stays
        // rejected would wedge every later save. Callers still see the rejection
        // through `result`.
        chainRef.current = result.catch(() => {});

        return result;
    }, [listingId, hasAddress]);

    const runBackgroundSave = useCallback(() => {
        // Background autosave failures are silent.
        enqueueSave().catch(() => {});
    }, [enqueueSave]);

    useEffect(() => {
        if (!listingId || !hasAddress) return;

        const signature = JSON.stringify(buildPayload(value));
        if (signature === lastSavedSignatureRef.current) return;

        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            runBackgroundSave();
        }, debounceMs);

        return () => clearTimeout(timerRef.current);
    }, [value, listingId, hasAddress, buildPayload, debounceMs, runBackgroundSave]);

    // Resolves only once the newest value is persisted: this save is queued
    // behind any in-flight one, so awaiting it also awaits that one. Rethrows,
    // so callers keep their isMissingAddressAutosaveError handling.
    const flush = useCallback(async (valueOverride) => {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        await enqueueSave(valueOverride);
    }, [enqueueSave]);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    return { flush };
}
